import { ContentContainer } from 'app/components/Core';
import { ContentContainerType } from 'types/enums';
import { Box, Flex, Text, VStack, Image } from '@chakra-ui/react';
import {
  PlaylistImage,
  PlaylistValidations,
  UpdatePlaylistData,
} from 'types/playlists.types';
import {
  getFormattedDurationForPlaylist,
  getFormattedFilesCount,
} from 'app/components/Playlist/utils';
import { PlaylistTitleForm } from 'app/components/Playlist/PlaylistTitleForm';
import { PlaylistHeaderImage } from 'app/components/Playlist/PlaylistHeaderImage';
import { AudioActionButton } from 'app/components/Song/Audio/AudioActionButton';
import { useAppSelector } from 'app/store';
import {
  getAudioPlayer,
  getPlayerCurrentAudio,
} from 'app/store/AudioPlayer/selectors';
import { useEffect, useMemo, useState } from 'react';
import { useAudioPlayer } from 'app/components/AudioPlayer/hooks';
import { PlaylistShare } from './PlaylistShare';

interface PlaylistRoomHeaderProps {
  guid: string;
  title: string;
  images?: PlaylistImage[];
  validations?: PlaylistValidations;
  onUpdate?: (data: UpdatePlaylistData) => Promise<void>;
  audios: {
    guid: string;
    title: string;
    duration: number;
  }[];
  loadPlaylistAudio: (playIndex: number) => Promise<void>;
  readonly?: boolean;
  playlistIconLink?: string;
  hideGradiant?: boolean;
}

export const PlaylistRoomHeader = ({
  images = [],
  guid,
  title,
  validations,
  onUpdate,
  audios,
  loadPlaylistAudio,
  readonly = false,
  playlistIconLink,
  hideGradiant = false,
}: PlaylistRoomHeaderProps) => {
  const totalDuration = audios.reduce((acc, curr) => acc + curr.duration, 0);
  const formattedDuration = getFormattedDurationForPlaylist(totalDuration);
  const formattedCount = getFormattedFilesCount(audios.length);

  const { isPlaying } = useAppSelector(getAudioPlayer);
  const currentAudio = useAppSelector(getPlayerCurrentAudio);
  const [playingAudio, setPlayingAudio] = useState(audios?.[0]);

  useEffect(() => {
    if (!currentAudio) {
      return;
    }

    const audio = audios?.find(({ guid }) =>
      [guid].includes(currentAudio?.audioGuid)
    );
    if (audio) {
      setPlayingAudio(audio);
    }
  }, [currentAudio, audios]);

  const audioGuid = useMemo(() => playingAudio?.guid, [playingAudio?.guid]);

  const image = useMemo(
    () => images.find((image) => image.name === 'playlist-small'),
    [images]
  );

  const { playPauseAudio } = useAudioPlayer();

  const handlePlay = async () => {
    if (!playingAudio) {
      return;
    }

    if (!audioGuid) {
      return;
    }

    if (currentAudio?.audioGuid === audioGuid) {
      playPauseAudio();
    } else {
      loadPlaylistAudio(0);
    }
  };

  const isLoading = useMemo(
    () => currentAudio?.audioGuid === audioGuid && currentAudio?.loading,
    [audioGuid, currentAudio?.audioGuid, currentAudio?.loading]
  );

  return (
    <Box bg="surface1" zIndex={1101} position="relative">
      {!hideGradiant && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            w: 'full',
            h: 'full',
            overflow: 'hidden',
            bg: 'M.30',
          }}
        >
          <Box
            sx={{
              h: 200,
              opacity: 0.36,
              filter: `blur(64px)`,
            }}
          >
            {image && (
              <Image
                src={image.href}
                w="100%"
                h="unset"
                mt="64px"
                transform="translate(0, -50%)"
                aspectRatio="1/1"
              />
            )}
          </Box>
        </Box>
      )}
      <ContentContainer type={ContentContainerType.LIST} py={6}>
        <Flex>
          <PlaylistHeaderImage
            images={images}
            playlistGuid={guid}
            readOnly={readonly}
            to={playlistIconLink}
          />
          <VStack pl={4} alignItems="baseline" spacing={0} flex={1}>
            <Text fontSize="xxs" fontWeight={500} opacity={0.72}>
              Playlist
            </Text>
            <PlaylistTitleForm
              title={title}
              validations={validations}
              onUpdate={onUpdate ? (title) => onUpdate({ title }) : undefined}
            />
            {audios.length > 0 && (
              <Text fontSize="xs">
                <span data-testid="file-count">{formattedCount}</span>
                &nbsp;•&nbsp;
                <span data-testid="total-duration">{formattedDuration}</span>
              </Text>
            )}
            <Flex gap={4} justifyContent="center" pt={2}>
              <AudioActionButton
                playing={isPlaying && currentAudio?.audioGuid === audioGuid}
                onClick={handlePlay}
                data-testid="audio-play-pause-button"
                isLoading={isLoading}
                size={32}
                isPlayerTheme
                playIconTestId="audio-play-icon"
                pauseIconTestId="audio-pause-icon"
                thickness={2}
                isDisabled={audios.length === 0}
              />
              {!readonly && <PlaylistShare guid={guid} title={title} />}
            </Flex>
          </VStack>
        </Flex>
      </ContentContainer>
    </Box>
  );
};
